import React from 'react';
import { bool } from 'prop-types';
import { useImpressionManager } from '@fiverr-private/impressionable';
import { useIsClient } from '@fiverr-private/hooks';
import { lazyImport } from '../../../apps/logged_out_homepage/utils/newLazyImport';
import Categories from '../../../apps/logged_out_homepage/components/Categories';
import Seo from '../../../apps/logged_out_homepage/shared/Seo';
import EntryWrapper from '../../../apps/logged_out_homepage/components/EntryWrapper';
import SubcategoryCarousel from '../../../apps/logged_out_homepage/components/SubcategoryCarousel';
import { getSubCategories } from '../../../apps/logged_out_homepage/components/SubcategoryCarousel/config';
import shouldShowLogoMakerBanner from '../../../apps/logged_out_homepage/utils/shouldShowLogoMakerBanner';
import { shouldLoadPersonalizedSection } from '../../../apps/logged_out_homepage/components/PersonalizedSectionWrapper/shouldLoad';
import VideoHeroBannerWrapper from '../../../apps/logged_out_homepage/components/VideoHeroBannerWrapper';
import VibeCodingBanner from '../../../apps/logged_out_homepage/components/VibeCodingBanner';
import { LAZY_COMPONENTS } from './constants';

const MadeOnFiverr = lazyImport(
    LAZY_COMPONENTS.MADE_ON_FIVERR,
    () => import(/* webpackChunkName: 'MadeOnFiverr' */ '../../../apps/logged_out_homepage/components/MadeOnFiverr')
);

const FiverrBusiness = lazyImport(
    LAZY_COMPONENTS.FIVERR_BUSINESS,
    () => import(/* webpackChunkName: 'FiverrBusiness' */ '../../../apps/logged_out_homepage/components/FiverrBusiness')
);

const FiverrGuides = lazyImport(
    LAZY_COMPONENTS.FIVERR_GUIDES,
    () => import(/* webpackChunkName: 'FiverrGuides' */ '../../../apps/logged_out_homepage/components/FiverrGuides')
);

const Signup = lazyImport(
    LAZY_COMPONENTS.SIGNUP,
    () => import(/* webpackChunkName: 'Signup' */ '../../../apps/logged_out_homepage/components/Signup')
);

const LogoMakerBanner = lazyImport(
    LAZY_COMPONENTS.LOGO_MAKER_BANNER,
    () =>
        import(/* webpackChunkName: 'LogoMakerBanner' */ '../../../apps/logged_out_homepage/components/LogoMakerBanner')
);

const PersonalizedSectionWrapper = lazyImport(
    LAZY_COMPONENTS.PERSONALIZED_SECTION_WRAPPER,
    () =>
        import(
            /* webpackChunkName: 'PersonalizedSectionWrapper' */ '../../../apps/logged_out_homepage/components/PersonalizedSectionWrapper'
        )
);

const Freelancers = lazyImport(
    LAZY_COMPONENTS.FREELANCERS,
    () => import(/* webpackChunkName: 'Freelancers' */ '../../../apps/logged_out_homepage/components/Freelancers')
);

const ProServices = lazyImport(
    LAZY_COMPONENTS.PRO_SERVICES,
    () => import(/* webpackChunkName: 'ProServices' */ '../../../apps/logged_out_homepage/components/ProServices')
);

const Video = lazyImport(
    LAZY_COMPONENTS.VIDEO,
    () => import(/* webpackChunkName: 'Video' */ '../../../apps/logged_out_homepage/components/Video')
);

const ProBanner = lazyImport(
    LAZY_COMPONENTS.PRO_BANNER,
    () => import(/* webpackChunkName: 'ProBanner' */ '../../../apps/logged_out_homepage/components/ProBanner')
);

const LoggedOutHomepageClient = ({
    userWasLoggedIn,
    displayBrandCampaign,
    inProPackagesBanner,
    inDolphinsTofRedirect,
    withEnglishBAL,
    inExpandableSearchBar,
    inLynxesProBanner,
}) => {
    useImpressionManager();
    const showLogoMakerBanner = shouldShowLogoMakerBanner();
    const isClient = useIsClient();

    const shouldLoad = isClient ? shouldLoadPersonalizedSection({ userWasLoggedIn }) : false;
    const shouldShowNewProBanner = inLynxesProBanner && withEnglishBAL;

    return (
        <EntryWrapper
            displayBrandCampaign={displayBrandCampaign}
            inDolphinsTofRedirect={inDolphinsTofRedirect}
            withEnglishBAL={withEnglishBAL}
        >
            <Seo />
            <VideoHeroBannerWrapper inExpandableSearchBar={inExpandableSearchBar} />
            <Categories />
            {shouldLoad && <PersonalizedSectionWrapper userWasLoggedIn={userWasLoggedIn} />}
            <SubcategoryCarousel subCategories={getSubCategories(withEnglishBAL)} />
            {withEnglishBAL && <VibeCodingBanner />}
            {shouldShowNewProBanner ? <ProBanner /> : <FiverrBusiness inProPackagesBanner={inProPackagesBanner} />}
            <Video />
            <ProServices />
            <Freelancers />
            {showLogoMakerBanner && <LogoMakerBanner />}
            <MadeOnFiverr />
            <FiverrGuides />
            <Signup />
        </EntryWrapper>
    );
};

LoggedOutHomepageClient.propTypes = {
    userWasLoggedIn: bool,
    displayBrandCampaign: bool,
    inProPackagesBanner: bool,
    inDolphinsTofRedirect: bool,
    withEnglishBAL: bool,
    inExpandableSearchBar: bool,
    inLynxesProBanner: bool,
};

export default LoggedOutHomepageClient;
